// Lightweight theme provider — sets a `.dark` class on <html> based on user
// choice (persisted in localStorage) or the OS prefers-color-scheme setting.
import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";

const STORAGE_KEY = "pb-theme"; // values: "light" | "dark" | "system" | "auto-night"
const NIGHT_START = 20; // 20:00
const NIGHT_END = 6;    // 06:00
const ThemeContext = createContext({ theme: "system", resolved: "light", setTheme: () => {} });

function detectSystem() {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function detectNight() {
  const h = new Date().getHours();
  return h >= NIGHT_START || h < NIGHT_END ? "dark" : "light";
}

function applyClass(resolved) {
  const root = document.documentElement;
  if (resolved === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

function resolveTheme(theme) {
  if (theme === "system") return detectSystem();
  if (theme === "auto-night") return detectNight();
  return theme;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => localStorage.getItem(STORAGE_KEY) || "system");
  const [resolved, setResolved] = useState(() => resolveTheme(localStorage.getItem(STORAGE_KEY) || "system"));
  const intervalRef = useRef(null);

  // Apply on mount + whenever theme changes
  useEffect(() => {
    const next = resolveTheme(theme);
    setResolved(next);
    applyClass(next);
  }, [theme]);

  // React to OS changes when in "system" mode
  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => {
      const next = e.matches ? "dark" : "light";
      setResolved(next);
      applyClass(next);
    };
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [theme]);

  // Re-evaluate every 5 minutes when in auto-night mode + on window focus
  useEffect(() => {
    if (theme !== "auto-night") {
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      return;
    }
    const tick = () => {
      const next = detectNight();
      setResolved((prev) => {
        if (prev !== next) applyClass(next);
        return next;
      });
    };
    tick();
    intervalRef.current = setInterval(tick, 5 * 60 * 1000); // 5 min
    const onFocus = () => tick();
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    };
  }, [theme]);

  const setTheme = useCallback((t) => {
    localStorage.setItem(STORAGE_KEY, t);
    setThemeState(t);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme }}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
